import AsyncSelect from '@/components/Form/Shared/AsyncSelect'
import pricingModelsHelp from '@/modules/Service/help/pricing-models-help.md'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import ConfigurationIcons from '@/icons/ConfigurationIcons'
import InfoIcons from '@/icons/InfoIcons'
import ServiceIcons from '@/icons/ServiceIcons'
import {
    PRICING_MODELS,
    SERVICE_PRICING_MODELS,
} from '@/modules/Contract/constants'
import {
    createOtherProduct,
    getProductByGroupName,
    getProductById,
    getServiceInteresting,
} from '@/modules/Contract/services'
import {
    Contract,
    GetProductByGroupNameResponse,
    ProductResponse,
} from '@/modules/Contract/types'
import { useAppStore } from '@/stores/useAppStore'
import usePermissionsStore from '@/stores/usePermissionsStore'
import { resources, scopes } from '@/utils/permission'
import { toDate } from '@/utils/function'
import { useQueryClient } from '@tanstack/react-query'
import dayjs from 'dayjs'
import { Plus } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { Path, useFormContext, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import toast from 'react-hot-toast'
import { Button, GridHookForm, Loading, Typogaphy } from 'vnetwork-ui'
import { FilterSelect } from '@/components/ui/filters'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'

interface Props {
    index: number
    isPOC?: boolean
    isAddon?: boolean
    allowPercentModel?: boolean
    fixedProductGroup?: string
    children: (product: ProductResponse, price: number) => React.ReactNode
}

const Base: React.FC<Props> = ({
    index,
    children,
    isPOC = false,
    isAddon = false,
    fixedProductGroup,
    allowPercentModel = false,
}) => {
    const formControl = useFormContext<Contract>()
    const { t } = useTranslation('contract')
    const queryClient = useQueryClient()
    const { setIsShowHelp, setHelpContent, isShowHelp } = useAppStore(
        (state) => state
    )
    const { isAdmin, permissions } = usePermissionsStore()

    // Who may work with the "Other/Khác" service group: Admin, or the roles that create customer
    // contracts (Sale/CSKH/BOM hold Customers create/update). Mirrors the API gate on
    // POST /products/other so the dropdown and the inline creator match what the backend allows.
    const canManageOtherService = useMemo(() => {
        if (isAdmin) return true
        const customerScopes =
            permissions.find((p) => p?.rsname === resources.customers)
                ?.scopes || []
        return (
            customerScopes.includes(scopes.create) ||
            customerScopes.includes(scopes.update)
        )
    }, [isAdmin, permissions])
    const [showOtherProductCreator, setShowOtherProductCreator] =
        useState(false)
    const [isCreatingOtherProduct, setIsCreatingOtherProduct] = useState(false)
    const [otherProductDraft, setOtherProductDraft] = useState({
        product_name: '',
    })

    const customer_id = useWatch({
        control: formControl.control,
        name: 'customer_id',
    })

    const productGroupName = useWatch({
        control: formControl.control,
        name: `order.products.[${index}].product_group_name` as any,
    }) as string

    const product_id = useWatch({
        control: formControl.control,
        name: `order.products.[${index}].product_id` as any,
    }) as number

    const servicePriceRegister = useWatch({
        control: formControl.control,
        name: `order.products.[${index}].service_price_register` as any,
    }) as number

    const effectiveProductGroup = fixedProductGroup || productGroupName
    const isOtherGroup =
        effectiveProductGroup?.toUpperCase?.() === 'OTHER' ||
        effectiveProductGroup?.toUpperCase?.() === 'KHÁC' ||
        effectiveProductGroup?.toUpperCase?.() === 'KHAC'
    const hasAutoSelectedService = useRef(false)
    const hasAutoSelectedProduct = useRef(false)
    const seededPriceProductId = useRef<number | null>(null)
    const prevProductGroup = useRef<string | null>(null)

    // Fetch service interesting options for auto-select
    const { data: serviceInterestingOptions } = useQueryWithHandler({
        queryKey: [
            QUERY_KEYS.SERVICE_INTERESTING,
            customer_id,
            isPOC,
            canManageOtherService,
        ],
        queryFn: () =>
            getServiceInteresting(customer_id, isPOC, canManageOtherService),
        enabled: !!customer_id && !fixedProductGroup,
    })

    // Auto-select first service interesting when available
    useEffect(() => {
        if (fixedProductGroup) return
        if (hasAutoSelectedService.current) return
        if (productGroupName) return
        if (!serviceInterestingOptions?.length) return

        const firstOption = serviceInterestingOptions[0]
        formControl.setValue(
            `order.products.[${index}].product_group_name` as Path<Contract>,
            firstOption.value
        )
        hasAutoSelectedService.current = true
    }, [
        serviceInterestingOptions,
        productGroupName,
        fixedProductGroup,
        formControl,
        index,
    ])

    const {
        data: products,
        isError,
        isPending,
        isFetching,
        renderState: renderProductsState,
    } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.PRODUCTS, effectiveProductGroup, isAddon],
        queryFn: () =>
            getProductByGroupName({
                group_name: effectiveProductGroup,
                is_addon: isAddon,
            }),
        enabled: !!effectiveProductGroup,
    })

    // Auto-select first product when products are loaded
    useEffect(() => {
        // Reset flag when product group changes
        if (effectiveProductGroup !== prevProductGroup.current) {
            hasAutoSelectedProduct.current = false
            prevProductGroup.current = effectiveProductGroup
        }

        if (hasAutoSelectedProduct.current) return
        if (isOtherGroup) return
        if (product_id) return
        if (!products?.results?.length) return

        const firstAvailableProduct = products.results.find(
            (p) => !p.product_hidden
        )
        if (!firstAvailableProduct) return

        formControl.setValue(
            `order.products.[${index}].product_id` as Path<Contract>,
            firstAvailableProduct.product_id
        )
        hasAutoSelectedProduct.current = true
    }, [
        products,
        product_id,
        effectiveProductGroup,
        formControl,
        index,
        isOtherGroup,
    ])

    const {
        data: productResponse,
        isPending: isPendingProduct,
        isError: isErrorProduct,
        renderState: renderProductState,
    } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.PRODUCT, product_id],
        queryFn: () => getProductById(product_id),
        enabled: !!product_id,
    })

    const productChoose = useMemo(() => {
        return productResponse?.result
    }, [productResponse])

    const isOtherStandardProduct = useMemo(() => {
        if (!productChoose) return false
        return (
            isOtherGroup ||
            productChoose.product_group_code?.toUpperCase?.() === 'OTHER'
        )
    }, [isOtherGroup, productChoose])

    const productForTemplate = useMemo(() => {
        if (!productChoose) return productChoose
        if (!isOtherStandardProduct) return productChoose

        return {
            ...productChoose,
            template_name: 'Other.Standard',
            product_config_options: [],
        }
    }, [isOtherStandardProduct, productChoose])

    const handleCancelChooseProduct = () => {
        seededPriceProductId.current = null
        formControl.setValue(
            `order.products.[${index}].service_configoptions` as Path<Contract>,
            []
        )
        formControl.setValue(
            `order.products.[${index}].service_price_register` as Path<Contract>,
            0
        )
        formControl.setValue(
            `order.products.[${index}].product_name` as Path<Contract>,
            ''
        )
        formControl.setValue(
            `order.products.[${index}].product_group_code` as Path<Contract>,
            ''
        )
        formControl.setValue(
            `order.products.[${index}].template_name` as Path<Contract>,
            ''
        )
        formControl.setValue(
            `order.products.[${index}].service_domain_metadata.other_standard_payment_schedule` as Path<Contract>,
            undefined
        )
    }

    const handleCreateOtherProduct = async () => {
        if (!otherProductDraft.product_name.trim()) {
            toast.error(t('other_standard.product_name_required'))
            return
        }

        setIsCreatingOtherProduct(true)
        try {
            const result = await createOtherProduct({
                product_name: otherProductDraft.product_name.trim(),
                currency: 'vi',
                start_date: toDate(dayjs()),
            })

            const productsQueryKey = [
                QUERY_KEYS.PRODUCTS,
                effectiveProductGroup,
                isAddon,
            ]
            queryClient.setQueryData(
                productsQueryKey,
                (
                    current:
                        | {
                              results?: GetProductByGroupNameResponse[]
                              [key: string]: unknown
                          }
                        | undefined
                ) => {
                    if (!current?.results) return current
                    const exists = current.results.some(
                        (product) =>
                            product.product_id === result.product.product_id
                    )
                    if (exists) return current

                    return {
                        ...current,
                        results: [
                            ...current.results,
                            {
                                product_id: result.product.product_id,
                                product_name: result.product.product_name,
                                product_hidden: false,
                            },
                        ],
                    }
                }
            )
            queryClient.invalidateQueries({
                queryKey: productsQueryKey,
                refetchType: 'none',
            })
            formControl.setValue(
                `order.products.[${index}].service_configoptions` as Path<Contract>,
                []
            )
            formControl.setValue(
                `order.products.[${index}].service_price_register` as Path<Contract>,
                0
            )
            formControl.setValue(
                `order.products.[${index}].product_group_code` as Path<Contract>,
                'OTHER'
            )
            formControl.setValue(
                `order.products.[${index}].template_name` as Path<Contract>,
                'Other.Standard'
            )
            formControl.setValue(
                `order.products.[${index}].service_domain_metadata.other_standard_payment_schedule` as Path<Contract>,
                undefined
            )
            formControl.setValue(
                `order.products.[${index}].product_id` as Path<Contract>,
                result.product.product_id
            )
            setShowOtherProductCreator(false)
            setOtherProductDraft({
                product_name: '',
            })
            toast.success(t('other_standard.product_created'))
        } catch {
            toast.error(t('other_standard.product_create_failed'))
        } finally {
            setIsCreatingOtherProduct(false)
        }
    }

    // Sync chosen product details into the form (price model, price, name, template)
    useEffect(() => {
        if (!productChoose) return
        const templateName = isOtherStandardProduct
            ? 'Other.Standard'
            : productChoose?.template_name || ''
        const setIfChanged = (
            name: Path<Contract>,
            value: string | number | unknown[]
        ) => {
            if (formControl.getValues(name) === value) return
            formControl.setValue(name, value as any)
        }

        setIfChanged(
            `order.products.[${index}].service_price_model` as Path<Contract>,
            productChoose?.product_price_model || PRICING_MODELS.Package
        )
        if (
            !isOtherStandardProduct &&
            seededPriceProductId.current !== productChoose.product_id
        ) {
            setIfChanged(
                `order.products.[${index}].service_price_register` as Path<Contract>,
                productChoose?.product_price || 0
            )
            seededPriceProductId.current = productChoose.product_id
        }
        setIfChanged(
            `order.products.[${index}].product_name` as Path<Contract>,
            productChoose?.product_name || ''
        )
        setIfChanged(
            `order.products.[${index}].product_group_code` as Path<Contract>,
            productChoose?.product_group_code || ''
        )
        setIfChanged(
            `order.products.[${index}].template_name` as Path<Contract>,
            templateName
        )

        if (templateName === 'Other.Standard') {
            const configOptionsName =
                `order.products.[${index}].service_configoptions` as Path<Contract>
            const currentConfigOptions =
                formControl.getValues(configOptionsName)

            if (currentConfigOptions?.length) {
                formControl.setValue(configOptionsName, [])
            }
        }
    }, [formControl, index, isOtherStandardProduct, productChoose])

    // Handle error states for both queries
    const productsStateComponent = renderProductsState()
    const productStateComponent = renderProductState()

    // Visibility class for the pricing-model picker and its help button
    const hasCustomPricingModelPicker =
        !!productChoose && !productChoose?.product_price_model
    let pricingModelPickerClass: string
    if (isPOC) {
        pricingModelPickerClass = 'hidden'
    } else if (hasCustomPricingModelPicker) {
        pricingModelPickerClass = 'col-span-2'
    } else {
        pricingModelPickerClass = 'hidden'
    }

    // Render product configuration section based on current state
    const renderProductConfiguration = () => {
        if (!product_id) {
            return (
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-6 text-center">
                    <div className="w-12 h-12 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-3">
                        <ConfigurationIcons className="w-6 h-6 text-slate-400" />
                    </div>
                    <Typogaphy className="text-slate-600 text-sm">
                        {t('please-select-a-product')}
                    </Typogaphy>
                </div>
            )
        }
        if (isPendingProduct) {
            return (
                <div className="bg-white border border-slate-200 rounded-lg p-8 text-center">
                    <div className="flex flex-col items-center gap-3">
                        <Loading.CircularProgress width={24} height={24} />
                        <Typogaphy className="text-slate-600 text-sm">
                            {t('loading-product-configuration')}
                        </Typogaphy>
                    </div>
                </div>
            )
        }
        if (productForTemplate) {
            return (
                <div className="space-y-4">
                    {children(productForTemplate, servicePriceRegister)}
                </div>
            )
        }
        return null
    }

    if (productsStateComponent && isError) {
        return (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                {productsStateComponent}
            </div>
        )
    }

    if (productStateComponent && isErrorProduct) {
        return (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                {productStateComponent}
            </div>
        )
    }

    return (
        <div className="space-y-4">
            {/* Product Selection Card */}
            <div className="bg-white rounded border border-slate-200 hover:border-slate-300 transition-colors duration-200">
                {/* Header */}
                <div className="p-4 py-2 bg-slate-50/50 border-b border-slate-200/60">
                    <div className="flex items-center gap-2.5">
                        <div className="w-6 h-6 bg-slate-100 rounded flex items-center justify-center flex-shrink-0">
                            <ServiceIcons className="w-3.5 h-3.5 text-slate-600" />
                        </div>
                        <Typogaphy className="font-medium text-slate-800 text-sm">
                            {t('product-selection')}
                        </Typogaphy>
                    </div>
                </div>

                {/* Product Selection Form */}
                <div className="p-4 pb-0">
                    <GridHookForm
                        formControl={formControl}
                        actions={[]}
                        subForm
                        data={[
                            ...(!fixedProductGroup
                                ? [
                                      {
                                          className: 'col-span-2',
                                          renderEditComponent: (
                                              control: any
                                          ) => {
                                              return (
                                                  <div data-tour="select-product-group">
                                                      <AsyncSelect
                                                          allowClear={false}
                                                          asyncOptions={() =>
                                                              getServiceInteresting(
                                                                  customer_id,
                                                                  isPOC,
                                                                  canManageOtherService
                                                              )
                                                          }
                                                          control={control}
                                                          name={
                                                              `order.products.[${index}].product_group_name` as Path<Contract>
                                                          }
                                                          dependencies={[
                                                              customer_id,
                                                          ]}
                                                          label={t(
                                                              'product_group_name_placeholder'
                                                          )}
                                                          placeholder={t(
                                                              'product_group_name_placeholder'
                                                          )}
                                                          onChangeAfterHookForm={() => {
                                                              handleCancelChooseProduct()
                                                              formControl.setValue(
                                                                  `order.products.[${index}].product_id` as Path<Contract>,
                                                                  0
                                                              )
                                                          }}
                                                      />
                                                  </div>
                                              )
                                          },
                                      },
                                  ]
                                : []),
                            {
                                formItemProps: {
                                    name: `order.products.[${index}].product_id` as Path<Contract>,
                                    type: 'select',
                                    options: products?.results?.map((p) => ({
                                        label: p?.product_name,
                                        value: p?.product_id,
                                        disabled: p?.product_hidden,
                                    })),
                                    disabled: isPending,
                                    loading: isFetching,
                                    label: t('product_placeholder'),
                                    placeholder: t('product_placeholder'),
                                    // No reset here: switching to a different product
                                    // re-seeds configs via Configuration's effect, while
                                    // re-selecting the same product must NOT wipe them
                                    // (the product detail query is cached, so the seed
                                    // effect won't re-run to restore them).
                                    allowClear: false,
                                    size: 'small',
                                    id: 'select-product',
                                },
                                className: fixedProductGroup
                                    ? 'col-span-4'
                                    : 'col-span-2',
                            },
                            {
                                className:
                                    isOtherGroup && canManageOtherService
                                        ? 'col-span-4'
                                        : 'hidden',
                                renderEditComponent: () => (
                                    <div className="space-y-3 rounded border border-slate-200 bg-slate-50/50 p-3">
                                        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                                            <Typogaphy className="text-sm font-medium text-slate-700">
                                                {t(
                                                    'other_standard.create_product_hint'
                                                )}
                                            </Typogaphy>
                                            <Button
                                                type="button"
                                                size="small"
                                                variant="outlined"
                                                startIcon={
                                                    <Plus className="h-4 w-4" />
                                                }
                                                onClick={(event) => {
                                                    event.preventDefault()
                                                    event.stopPropagation()
                                                    setShowOtherProductCreator(
                                                        (value) => !value
                                                    )
                                                }}
                                            >
                                                {t(
                                                    'other_standard.create_product'
                                                )}
                                            </Button>
                                        </div>

                                        {showOtherProductCreator && (
                                            <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
                                                <label className="space-y-1 md:col-span-2">
                                                    <span className="text-xs text-slate-600">
                                                        {t(
                                                            'other_standard.product_name'
                                                        )}
                                                    </span>
                                                    <input
                                                        className="h-9 w-full rounded border border-slate-200 px-3 text-sm"
                                                        value={
                                                            otherProductDraft.product_name
                                                        }
                                                        onChange={(event) =>
                                                            setOtherProductDraft(
                                                                (draft) => ({
                                                                    ...draft,
                                                                    product_name:
                                                                        event
                                                                            .target
                                                                            .value,
                                                                })
                                                            )
                                                        }
                                                        onKeyDown={(event) => {
                                                            if (
                                                                event.key !==
                                                                'Enter'
                                                            )
                                                                return

                                                            event.preventDefault()
                                                            event.stopPropagation()
                                                            handleCreateOtherProduct()
                                                        }}
                                                    />
                                                </label>
                                                <div className="flex items-end">
                                                    <Button
                                                        type="button"
                                                        size="small"
                                                        variant="contained"
                                                        className="w-full"
                                                        disabled={
                                                            isCreatingOtherProduct
                                                        }
                                                        onClick={(event) => {
                                                            event.preventDefault()
                                                            event.stopPropagation()
                                                            handleCreateOtherProduct()
                                                        }}
                                                    >
                                                        {t('create')}
                                                    </Button>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                ),
                            },
                            {
                                renderEditComponent: (control) => (
                                    <div data-tour="pricing-model">
                                        <FilterSelect
                                            control={control}
                                            name={
                                                `order.products.[${index}].service_price_model` as Path<Contract>
                                            }
                                            label={t(
                                                'pricing_model_placeholder'
                                            )}
                                            placeholder={t(
                                                'pricing_model_placeholder'
                                            )}
                                            options={SERVICE_PRICING_MODELS(
                                                t
                                            ).filter((item) => {
                                                if (!allowPercentModel) {
                                                    return (
                                                        item.value !==
                                                        'percent_per_parent'
                                                    )
                                                }
                                                return true
                                            })}
                                            showClearButton={false}
                                        />
                                    </div>
                                ),
                                className: pricingModelPickerClass,
                            },
                            {
                                className: hasCustomPricingModelPicker
                                    ? 'col-span-2 h-full'
                                    : 'hidden',
                                renderEditComponent: () => {
                                    return (
                                        <Button
                                            variant="outlined"
                                            type="button"
                                            size="small"
                                            startIcon={
                                                <InfoIcons className="w-4 h-4" />
                                            }
                                            onClick={() => {
                                                setIsShowHelp(!isShowHelp)
                                                setHelpContent(
                                                    pricingModelsHelp
                                                )
                                            }}
                                            className="w-full"
                                        >
                                            <Typogaphy>
                                                {t('pricing_model_guide')}
                                            </Typogaphy>
                                        </Button>
                                    )
                                },
                            },
                        ]}
                    />
                </div>
            </div>

            {/* Product Configuration Section */}
            {renderProductConfiguration()}
        </div>
    )
}

export default Base
